import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, Star, X, ImageIcon, Link } from 'lucide-react';
import { cities, getDistricts, facilityFeatures, sportBranches } from '@/data/turkeyData';
import { FACILITY_TYPES } from '@/data/facilityTypes';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const AddFacilityForm = ({ onSuccess, onCancel, initialData = null, endpoint }) => {
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    facility_type: 'hali_saha',
    city: '',
    district: '',
    address: '',
    google_maps_link: '',
    price_per_hour: '',
    features: [],
    branches: [],
    description: '',
    phone: '',
    cancellation_policy: 'Rezervasyon saatine 24 saat kala iptal edilebilir.',
    images: [],
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        facility_type: initialData.facility_type || 'hali_saha',
        city: initialData.city || '',
        district: initialData.district || '',
        address: initialData.address || '',
        google_maps_link: initialData.google_maps_link || '',
        price_per_hour: initialData.price_per_hour || '',
        features: initialData.features || [],
        branches: initialData.branches || [],
        description: initialData.description || '',
        phone: initialData.phone || '',
        cancellation_policy: initialData.cancellation_policy || 'Rezervasyon saatine 24 saat kala iptal edilebilir.',
        images: (initialData.images || []).map((url, i) => ({ url, isMain: i === 0 })),
        latitude: initialData.latitude || '',
        longitude: initialData.longitude || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (formData.city) {
      setAvailableDistricts(getDistricts(formData.city));
    }
  }, [formData.city]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    for (const file of files) {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, { url: e.target.result, isMain: prev.images.length === 0 }]
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Image processing error:', error);
      }
    }
    setUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      if (prev.images[index]?.isMain && newImages.length > 0) newImages[0].isMain = true;
      return { ...prev, images: newImages };
    });
  };

  const setMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({ ...img, isMain: i === index }))
    }));
  };

  const toggleFeature = (id) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(id) 
        ? prev.features.filter(f => f !== id) 
        : [...prev.features, id]
    }));
  };

  const toggleBranch = (id) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.includes(id) 
        ? prev.branches.filter(b => b !== id) 
        : [...prev.branches, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sortedImages = [...formData.images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
    
    const payload = {
      ...formData,
      price_per_hour: parseFloat(formData.price_per_hour),
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      images: sortedImages.map(img => img.url)
    };

    const finalEndpoint = endpoint || (initialData ? `${API}/operator/facilities/${initialData.facility_id}` : `${API}/operator/facilities`);
    const method = initialData ? 'put' : 'post';

    try {
      await axios[method](finalEndpoint, payload, { withCredentials: true });
      toast.success(initialData ? 'Tesis güncellendi' : 'Tesis eklendi');
      onSuccess();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const facilityTypes = FACILITY_TYPES;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <Label>Tesis Adı *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tesis Türü *</Label>
          <Select 
            value={formData.facility_type} 
            onValueChange={(v) => setFormData({...formData, facility_type: v})}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {facilityTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Saatlik Ücret (₺) *</Label>
          <Input
            type="number"
            value={formData.price_per_hour}
            onChange={(e) => setFormData({...formData, price_per_hour: e.target.value})}
            required
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Şehir *</Label>
          <Select 
            value={formData.city} 
            onValueChange={(v) => setFormData({...formData, city: v, district: ''})}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>İlçe *</Label>
          <Select 
            value={formData.district} 
            onValueChange={(v) => setFormData({...formData, district: v})}
            disabled={!formData.city}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableDistricts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Adres *</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          required
          className="mt-1"
        />
      </div>

      <div>
        <Label>Telefon</Label>
        <Input
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          className="mt-1"
          placeholder="05XX..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Enlem (Latitude)</Label>
          <Input
            type="number"
            step="any"
            value={formData.latitude}
            onChange={(e) => setFormData({...formData, latitude: e.target.value})}
            className="mt-1"
            placeholder="39.9334"
          />
        </div>
        <div>
          <Label>Boylam (Longitude)</Label>
          <Input
            type="number"
            step="any"
            value={formData.longitude}
            onChange={(e) => setFormData({...formData, longitude: e.target.value})}
            className="mt-1"
            placeholder="32.8597"
          />
        </div>
      </div>

      <div>
        <Label className="mb-3 block">Özellikler</Label>
        <div className="grid grid-cols-3 gap-2">
          {facilityFeatures.map(f => (
            <label key={f.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
              <Checkbox checked={formData.features.includes(f.id)} onCheckedChange={() => toggleFeature(f.id)} />
              <span className="text-sm">{f.icon} {f.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label className="mb-3 block">Spor Dalları</Label>
        <div className="grid grid-cols-3 gap-2">
          {sportBranches.map(b => (
            <label key={b.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
              <Checkbox checked={formData.branches.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
              <span className="text-sm">{b.icon} {b.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>İptal Koşulları</Label>
        <Textarea
          value={formData.cancellation_policy}
          onChange={(e) => setFormData({...formData, cancellation_policy: e.target.value})}
          rows={2}
          className="mt-1"
          placeholder="Örn: 24 saat kala iptal edilebilir."
        />
      </div>

      <div>
        <Label>Açıklama</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          rows={3}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="mb-2 block">Fotoğraflar</Label>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {formData.images.map((img, i) => (
            <div key={i} className="relative aspect-video border rounded overflow-hidden group">
              <img src={img.url} className="w-full h-full object-cover" alt="" />
              {img.isMain && <span className="absolute top-1 left-1 bg-emerald-500 text-white text-xs px-1 rounded">Ana</span>}
              <div className="absolute top-1 right-1 flex gap-1">
                <button type="button" onClick={() => setMainImage(i)} className="p-1 bg-white rounded hover:bg-yellow-100"><Star size={12} /></button>
                <button type="button" onClick={() => removeImage(i)} className="p-1 bg-red-500 text-white rounded hover:bg-red-600"><X size={12} /></button>
              </div>
            </div>
          ))}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} className="w-full">
          <Upload className="w-4 h-4 mr-2" /> Fotoğraf Yükle
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>İptal</Button>}
        <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">Kaydet</Button>
      </div>
    </form>
  );
};

export default AddFacilityForm;
